import { useState } from 'react'
import { Waypoint } from 'react-waypoint'

import { type ConfirmAwardedLaneAction } from '../domain/ConfirmAwardedLaneAction'
import { type Lane } from '../domain/Lane'
import { type Proposal } from '../domain/Proposal'
import { AwardedLaneItem } from './AwardedLaneItem'

export type AwardedLanesListProps = {
  rfpId: string
  awardedProposal: Proposal
  isConfirmingSingleLane: boolean
  isDisabled: boolean
  handleConfirmLane: (laneId: number, action: ConfirmAwardedLaneAction) => void
}

function getLaneKey(lane: Lane) {
  /**
   * The "model" that we expect in the UI after using MultiMode
   * is to have the same lane, "duplicated" N times, one for each preference/proposal.
   * So, we need to differentiate them by the proposal.id to avoid react key warnings.
   */
  return `${lane.id}-${lane.laneProposal?.id}`
}

const BATCH_INCREMENT = 50

export function AwardedLanesList({
  rfpId,
  awardedProposal,
  isConfirmingSingleLane,
  isDisabled,
  handleConfirmLane,
}: AwardedLanesListProps) {
  const [visibleLanesCount, setVisibleLanesCount] = useState(BATCH_INCREMENT)

  const { lanes } = awardedProposal
  const { distanceUnit } = awardedProposal.rfp

  const [confirmingLaneId, setConfirmingLaneId] = useState<number | undefined>()

  function handleConfirm(laneId: number, action: ConfirmAwardedLaneAction) {
    setConfirmingLaneId(laneId)
    handleConfirmLane(laneId, action)
  }

  function isConfirming({ laneProposal }: Lane) {
    return laneProposal!.id === confirmingLaneId && isConfirmingSingleLane
  }

  return (
    <>
      {lanes.slice(0, visibleLanesCount).map((lane, index) => (
        <AwardedLaneItem
          rfpId={rfpId}
          position={index}
          awardedLane={lane}
          key={getLaneKey(lane)}
          isDisabled={isDisabled}
          handleConfirmLane={handleConfirm}
          isConfirming={isConfirming(lane)}
          distanceUnit={distanceUnit}
        />
      ))}
      <Waypoint
        onEnter={() => {
          /**
           * @TODO: Review this solution
           *
           * "Quick and naive" solution to avoid rendering lanes all lanes at once.
           * Having many lanes was causing the page to freeze in some cases.
           * So, we are rendering only the first 50 lanes and then incrementing 50 more each time,
           * levering React capacity to not render/commit again, based in the key, the same items.
           *
           * This will be refactored in future work to implement pagination.
           */
          if (visibleLanesCount < lanes.length) {
            setVisibleLanesCount(visibleLanesCount + BATCH_INCREMENT)
          }
        }}
      />
    </>
  )
}
